import { useEffect } from "react"
import { useGoogleAnalytics } from "./hooks/useGoogleAnalytics.js"
import { useAndroidBackButton } from "./hooks/useAndroidBackButton.js"
import { usePushNotifications } from "./hooks/usePushNotifications.js"
import { useOnboardingSync } from "./hooks/useOnboardingSync.js"
import { useAppInstallUpgrade } from "./hooks/useAppInstallUpgrade.js"
import { useSyncLocaleToBackend } from "./i18n/useSyncLocaleToBackend.js"

const AppGlobalHooks = () => {
  // Both hooks self-guard internally: useGoogleAnalytics no-ops on native,
  // useAndroidBackButton no-ops off Android. Call them unconditionally so the
  // Android back-button listener actually registers (an early native return
  // here previously skipped it) and to respect the Rules of Hooks.
  useGoogleAnalytics()
  useAndroidBackButton()
  // Registers native push listeners once authenticated (no-op on web).
  usePushNotifications()
  // Pushes onboarding choices to the backend on the first authenticated session.
  useOnboardingSync()
  // Grants the app tier once authenticated on native shells (no-op on web).
  useAppInstallUpgrade()
  // Mirrors the UI language to the backend so notifications use it.
  useSyncLocaleToBackend()

  useEffect(() => {
    // WebMCP: expose site tools to AI agents in supported browsers
    const nav = navigator as any
    if (!nav?.modelContext) return
    const controller = new AbortController()
    const signal = controller.signal
    const registerTool = nav.modelContext.registerTool?.bind(nav.modelContext)
    if (!registerTool) return
    registerTool({
      name: "browse_dogs",
      description: "Browse dogs available for adoption in Portugal",
      inputSchema: { type: "object", properties: { city: { type: "string", description: "Filter by city name" } } },
      execute: ({ city }: { city?: string }) => { window.location.href = city ? `/caes?city=${encodeURIComponent(city)}` : "/caes" },
    }, { signal })
    registerTool({
      name: "browse_cats",
      description: "Browse cats available for adoption in Portugal",
      inputSchema: { type: "object", properties: { city: { type: "string", description: "Filter by city name" } } },
      execute: ({ city }: { city?: string }) => { window.location.href = city ? `/gatos?city=${encodeURIComponent(city)}` : "/gatos" },
    }, { signal })
    registerTool({
      name: "find_kennels",
      description: "Find animal shelters and rescue kennels in Portugal",
      inputSchema: { type: "object", properties: {} },
      execute: () => { window.location.href = "/canis" },
    }, { signal })
    registerTool({
      name: "start_pawmatch",
      description: "Start the PawMatch swipe-to-adopt experience to find the perfect animal",
      inputSchema: { type: "object", properties: {} },
      execute: () => { window.location.href = "/pawmatch" },
    }, { signal })
    return () => controller.abort()
  }, [])

  return null
}

export default AppGlobalHooks
